import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FileService } from 'src/app/services/file.service';
import { CampaignHelperService } from '../../../services/campaign-helper.service';
import { UploadValidationModalComponent } from './upload-validation-modal/upload-validation-modal.component';

@Injectable({
    providedIn: 'root'
})
export class UploadService {
    maxFileSize = 50 * 1024 * 1024;
    documentsSizes = 0;

    constructor(
        private campaignHelperService: CampaignHelperService,
        private fileService: FileService,
        private dialog: MatDialog
    ) { }

    validateAndProcessMediaFile(event: any, resolution: string, callback: (file: File) => void, idGroup?: string, mediasData?: any) {
        if (!event.target.files?.length) return;

        const [file] = event.target.files;
        const [maxWidth, maxHeight] = resolution.split('x').map(Number);
        const allowedTypes = ['image/png', 'image/jpeg', 'video/mp4'];

        if (!allowedTypes.includes(file.type)) {
            this.showDialogUploadValidation('Please upload a file with the required format - Images or Videos ( JPG | PNG | MP4 )');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e: any) => {
            if (file.type === 'video/mp4') {
                this.validateVideo(e.target.result, file, maxWidth, maxHeight, resolution, idGroup, mediasData, callback);
            } else {
                this.validateImage(e.target.result, file, maxWidth, maxHeight, callback);
            }
        };
        reader.readAsDataURL(file);
    }

    private validateVideo(src: string, file: File, maxW: number, maxH: number, resolution: string, idGroup: any, mediasData: any, callback: Function) {
        const video = document.createElement('video');
        video.src = src;
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
            const maxDuration = this.getChannelsDurationByResolution(resolution, mediasData);
            window.URL.revokeObjectURL(video.src);

            if (video.videoHeight === maxH && video.videoWidth === maxW) {
                if (video.duration <= maxDuration + 0.1) {
                    callback(file);
                } else {
                    this.showDialogUploadValidation(`Your video length is higher than the allowed duration. (${maxDuration} seconds)`);
                }
            } else {
                this.showDialogUploadValidation(`Please upload a video with the required resolution ${maxW} x ${maxH}`);
            }
        };
    }

    private validateImage(src: string, file: File, maxW: number, maxH: number, callback: Function) {
        const image = new Image();
        image.src = src;
        image.onload = (rs: any) => {
            if (rs.currentTarget['height'] === maxH && rs.currentTarget['width'] === maxW) {
                callback(file);
            } else {
                this.showDialogUploadValidation(`Please upload a picture with the required resolution ${maxW} x ${maxH}`);
            }
        };
    }

    uploadMedia(file: File) {
        return this.campaignHelperService.uploadMedia(file);
    }

    handleDocumentSelection(event: any, currentFiles: File[]): { files: File[], exceeded: boolean, newTotalSize: number } {
        const newFiles = event.target.files;
        const result = { files: [...currentFiles], exceeded: false, newTotalSize: this.documentsSizes };

        for (let i = 0; i < newFiles.length; i++) {
            const doc = newFiles[i];
            if (doc.size > this.maxFileSize || this.documentsSizes + doc.size > this.maxFileSize) {
                result.exceeded = true;
                break;
            }
            this.documentsSizes += doc.size;
            result.files.push(doc);
        }

        result.newTotalSize = this.documentsSizes;
        return result;
    }

    removeFile(index: number, files: File[]): { files: File[], newTotalSize: number } {
        const fileSizeToRemove = files[index].size;
        this.documentsSizes -= fileSizeToRemove;
        files.splice(index, 1);
        return { files, newTotalSize: this.documentsSizes };
    }

    downloadFile(filename: string) {
        this.fileService.downloadFile(filename).subscribe(blob => {
            this.createDownloadLink(blob, filename);
        });
    }

    createDownloadLink(blob: Blob, fileName: string) {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    }

    private getChannelsDurationByResolution(resolution: string, mediasData: any) {
        let effectiveRes = resolution;
        const channelInfo = mediasData.get(effectiveRes)[0];
        return channelInfo.groups[0].loopDuration / channelInfo.groups[0].nbrSlotsPerLoop;
    }

    private showDialogUploadValidation(message: string) {
        this.dialog.open(UploadValidationModalComponent, {
            height: '250px',
            width: '600px',
            data: { message }
        });
    }
}